import { PaintTarget } from '@ff-os/rendering-shared/targets';
import { calculateCoverCropForFfCanvas } from '@ff-os/shared-utils/crop/calculateCoverCropForFfCanvas';
import { Area } from '@ff-os/shared-utils/types/Area';
import { degreesToRadians } from '@ff-os/shared-utils/units';
import { rotateAroundOrigin } from '@ff-os/shared-utils/vector2d';
import { Photo } from 'ff-canvas';
import { PhotoPrintsJson } from './PhotoPrintsJson';
import { getRotationSettings, RotationSettings } from './rotationSettings';
import { StrokeSettings } from './StrokeSettings';
import { toPhotoUrl } from './toPhotoUrl';

export const toPaintable = (
    photo: PhotoPrintsJson.ProcessedPhoto,
    size: PhotoPrintsJson.Size,
    jwt: string | null,
    target: PaintTarget,
    border?: StrokeSettings,
    group?: PhotoPrintsJson.Group
): Photo => {
    const imageSize = {
        width: photo.width,
        height: photo.height
    };

    let adjustedSize = size;

    const hasPhotoFunBorder = group && typeof group.magicBorderId === 'string' && group.magicBorderId.trim() !== '';
    const hasPattern = group && typeof group.patternId === 'string' && group.patternId.trim() !== '';
    const hasBorderApplied = border?.size || hasPhotoFunBorder || hasPattern;

    if (size?.borderAspectRatio && hasBorderApplied) {
        const photoPrintsSize = size as PhotoPrintsJson.Size;
        const [widthStr, heightStr] = photoPrintsSize.borderAspectRatio!.split('/');
        const overrideWidth = parseFloat(widthStr);
        const overrideHeight = parseFloat(heightStr);

        if (!isNaN(overrideWidth) && !isNaN(overrideHeight) && overrideHeight !== 0) {
            // Use the exact aspect ratio from border-aspect-ratio
            const targetAspectRatio = overrideWidth / overrideHeight;

            // Calculate the new dimensions while maintaining the specified aspect ratio
            const sizeAspectRatio = photoPrintsSize.width / photoPrintsSize.height;

            if (targetAspectRatio > sizeAspectRatio) {
                // Target is wider, fit to width
                adjustedSize = {
                    ...photoPrintsSize,
                    width: photoPrintsSize.width,
                    height: photoPrintsSize.width / targetAspectRatio
                };
            } else {
                // Target is taller, fit to height
                adjustedSize = {
                    ...photoPrintsSize,
                    width: photoPrintsSize.height * targetAspectRatio,
                    height: photoPrintsSize.height
                };
            }
        }
    }

    // Calculate rotation settings AFTER adjusting size for forced aspect ratio
    const rotationSettings = getRotationSettings(adjustedSize, photo);
    const imageContainer = toImageContainer(adjustedSize, rotationSettings, border);
    const cropOverrides = calculateCoverCropForFfCanvas(
        imageContainer,
        imageSize, photo.crop,
        photo.autoFaceCenter,
        photo.detections);

    return {
        ...createPhoto(photo, imageContainer, jwt, rotationSettings, border, adjustedSize, group),
        ...cropOverrides,
        ...toPrintOverrides(target)
    };
};

export const toPaintableWithoutCropping = (
    photo: PhotoPrintsJson.ProcessedPhoto,
    size: { width: number; height: number },
    jwt: string | null
) => {

    const area: Area = {
        width: size.width,
        height: size.height,
        x: -0.5 * size.width,
        y: -0.5 * size.height
    };

    return createPhoto(photo, area, jwt);
};

const createPhoto = (
    photo: PhotoPrintsJson.ProcessedPhoto,
    imageContainer: Area,
    jwt: string | null,
    settings?: RotationSettings,
    strokeSettings?: StrokeSettings,
    size?: PhotoPrintsJson.Size,
    group?: PhotoPrintsJson.Group
): Photo => {

    const angle = settings ? settings.imageOrientation : 0;

    if (!strokeSettings) {
        strokeSettings = {color: '#fff', size: 0};
    }

    // Get offsets from size if available
    const offsetX = (size as PhotoPrintsJson.Size)?.offsetX ?? 0;
    const offsetY = (size as PhotoPrintsJson.Size)?.offsetY ?? 0;

    return ({

        id: photo.id,
        type: 'photo',
        round: true,

        url: toPhotoUrl(photo, jwt)!,

        x: calculateXBasedOnAngle(imageContainer.x, angle, strokeSettings) + offsetX,
        y: imageContainer.y - strokeSettings.size + offsetY,
        width: imageContainer.width,
        height: imageContainer.height,
        zIndex: 0,
        angle: degreesToRadians(angle),

        scaleX: 1,
        scaleY: 1,
        alpha: 1,

        filter: photo.filter || null,
        brightness: 1,
        saturation: 1,
        contrast: 1,

        clipPaths: [],
        clipViewBoxWidth: 0,
        clipViewBoxHeight: 0,

        frameUrl: null,
        maskUrl: null,

        innerOffsetX: 0,
        innerOffsetY: 0,
        innerZoom: 1,
        innerAngle: 0,

        flipX: false,
        flipY: false,

        shadow: false,
        shadowBlur: 0,
        shadowColor: '#FFF',
        shadowOffsetX: 0,
        shadowOffsetY: 0,

        stroke: strokeSettings.size > 0,
        strokeSize: Math.round(strokeSettings.size),
        strokeStyle: strokeSettings.color,

        fit: 'cover',

        custom: {
            watch: [],
            values: {
                magicBorderId: group?.magicBorderId,
                magicBorderRngSeed: photo.magicBorderRngSeed,
                reproString: photo.reproString,
                magicBorderAssetId: group?.magicBorderAssetId,
                patternId: group?.patternId,
                patternScale: size?.patternScale ?? 1,
                designOverridePortrait: size?.designOverridePortrait ?? null,
                designOverrideLandscape: size?.designOverrideLandscape ?? null
            }
        }
    });
};

const toImageContainer = (size: PhotoPrintsJson.Size | { width: number; height: number }, settings: RotationSettings, strokeSettings?: StrokeSettings) => {

    const width = (settings.rotateImage ? size.height : size.width) - ((strokeSettings?.size ?? 0) * 2);
    const height = (settings.rotateImage ? size.width : size.height) - ((strokeSettings?.size ?? 0) * 2) ;
    const imageAngle = settings.imageOrientation;

    const topLeft = {
        x: -0.5 * width,
        y: -0.5 * height
    };

    const center = {
        x: topLeft.x + 0.5 * width,
        y: topLeft.y + 0.5 * height
    };

    const rotatedTopLeft = rotateAroundOrigin(topLeft, center, imageAngle);

    return {
        x: rotatedTopLeft.x,
        y: rotatedTopLeft.y,
        width: width,
        height: height
    };
};

const toPrintOverrides = (target: PaintTarget): Partial<Photo> => {

    if (target === 'print') {
        return {
            svgOptions: {
                encoding: 'jpeg',
                quality: 0.9
            }
        };
    }

    return {};
};

const calculateXBasedOnAngle = (x: number, angle: number, strokeSettings?: StrokeSettings) : number => {

    if (!strokeSettings) {
        return x;
    }

    if (angle === 0) {
        return x - strokeSettings.size;
    } else {
        return x + strokeSettings.size;
    }
};